/**
 * Home scene data mapping — transforms API responses (RunBundleSummary,
 * ActiveRunSummary) into render-ready HomeDoc shapes. Pure, no-side-effects
 * derivations used by HomeScene.tsx.
 *
 * Per landingpage.txt §5.1: "Find the existing source, map field-by-field,
 * flag rather than fabricate." Non-existent fields (gaps, notes, owner,
 * ownerBg, state enum, real firstName) are marked with TODO comments and
 * hardcoded absent — HomeDocCard hides these elements per spec rules,
 * no fabrication.
 */

import type { ActiveRunSummary, RunBundleSummary } from "../api/client";
import { codeForBackendDocType } from "./doc-catalog";
import { brandColors } from "./tokens";

export type DocState = "drafting" | "review" | "indexing" | "done";

export interface HomeDoc {
  runId: string;
  docId: string;
  live: boolean;
  /** Display code for the doctype chip (UI code, e.g. "CMC-ANDA"). NOT usable
   *  for API calls — see ``backendDocType``. */
  doctype: string;
  /** The raw backend doc_type (e.g. "CMC_ANDA_SMALL_MOLECULE"). This is what
   *  ``getDocTypeConfig`` / ``/doc-types/{id}`` and ``RunLoadContext.docType``
   *  expect — the display ``doctype`` code (e.g. "CMC-ANDA") 404s the endpoint. */
  backendDocType: string;
  /** The run's real library id/name (from RunBundleSummary/ActiveRunSummary).
   *  Handed to ``handleRunLoaded`` via ``RunLoadContext`` so the doctype +
   *  library chips restore on Load. Null when the list row didn't carry it
   *  (legacy rows) — the shell then falls back to Word custom-props. */
  libraryId: string | null;
  libraryName: string | null;
  title: string;
  sub: string;
  state: DocState;
  stateLabel: string;
  progressLabel: string;
  pct: number;
  gaps: number;
  notes: number;
  owner: string | null;
  ownerBg: string | null;
  current: boolean;
  /** Run creation timestamp (ISO 8601), for the "created" line. Null when the
   *  list endpoint didn't carry it (legacy rows). */
  createdAt: string | null;
  /** Total input/output tokens across the run. 0 when unavailable. */
  inputTokens: number;
  outputTokens: number;
  /** The folder this document is filed in (from the run's ``folder_id``), or
   *  null when unfiled. Home groups cards by this. */
  folderId: string | null;
}

export function mapRunStatusToDocState(status: RunBundleSummary["status"] | ActiveRunSummary["status"]): DocState {
  switch (status) {
    case "running":
      return "drafting";
    case "paused":
      return "review";
    case "pending":
      return "indexing";
    case "completed":
    case "failed":
    case "cancelled":
      return "done";
    default:
      return "done";
  }
}

function statusToLabel(status: RunBundleSummary["status"] | ActiveRunSummary["status"]): string {
  switch (status) {
    case "running":
      return "Drafting now";
    case "paused":
      return "Paused — review needed";
    case "pending":
      return "Queued";
    case "completed":
      return "Completed";
    case "failed":
      return "Failed";
    case "cancelled":
      return "Cancelled";
    default:
      return "—";
  }
}

export function mapRunsToHomeDocs(
  completedGroups: Array<{ doc_id: string; folder_id?: string | null; runs: RunBundleSummary[] }>,
  activeRuns: ActiveRunSummary[],
  currentDocId: string | null,
  username: string | null = null,
  // When false (the empty-doc Home default) each document collapses to a
  // single card built from its most-recent run. When true (the Drafts tab)
  // EVERY completed run becomes its own card — the cross-run revision history
  // the Drafts surface is built around — keyed by run_id so same-doc runs
  // don't clobber each other.
  expandRuns = false,
): HomeDoc[] {
  const byDocId = new Map<string, HomeDoc>();

  // Derive owner initials from username (for avatar display)
  const ownerInitials = deriveInitials(username);
  const ownerBgColor = brandColors.mid;

  const isCurrent = (docId: string): boolean => Boolean(currentDocId) && docId === currentDocId;

  for (const group of completedGroups) {
    const runs = expandRuns ? group.runs : group.runs.slice(0, 1);
    for (const run of runs) {
      if (!run) continue;

      // Collapsed mode keys by doc_id (one card per doc); expanded mode keys
      // by run_id so every run in the group survives.
      const key = expandRuns ? `run:${run.run_id}` : group.doc_id;
      byDocId.set(key, {
        runId: run.run_id,
        docId: group.doc_id,
        live: false,
        doctype: codeForBackendDocType(run.doc_type) ?? run.doc_type,
        backendDocType: run.doc_type,
        libraryId: run.library_id ?? null,
        libraryName: run.library_name?.trim() || null,
        title: run.name?.trim() || run.title || group.doc_id,
        sub: run.library_name?.trim() || run.project_name || "",
        state: mapRunStatusToDocState(run.status),
        stateLabel: statusToLabel(run.status),
        // Denominator is the whole submission (every section in the doc type),
        // not the run's selected scope. Fall back to the scoped total when the
        // server can't resolve the doc type (sections_total 0/absent).
        progressLabel:
          (run.sections_total || run.modules_total) > 0
            ? `${run.modules_completed} of ${run.sections_total || run.modules_total} sections`
            : "—",
        pct:
          (run.sections_total || run.modules_total) > 0
            ? Math.round((run.modules_completed / (run.sections_total || run.modules_total)) * 100)
            : 0,
        // Gaps/notes are not carried by the list endpoints — never fabricate
        // them here (they read as real counts on the card). 0 ⇒ pills hidden.
        gaps: 0,
        notes: 0,
        owner: ownerInitials,
        ownerBg: ownerBgColor,
        current: isCurrent(group.doc_id),
        createdAt: run.created_at ?? run.completed_at ?? null,
        inputTokens: run.total_input_tokens ?? 0,
        outputTokens: run.total_output_tokens ?? 0,
        folderId: run.folder_id ?? group.folder_id ?? null,
      });
    }
  }

  // The same live run can arrive from both the user-scoped and the
  // workspace-scoped feed — dedupe by run_id so it maps to one card.
  const seenRunIds = new Set<string>();
  for (const run of activeRuns) {
    if (seenRunIds.has(run.run_id)) continue;
    seenRunIds.add(run.run_id);

    const docId = run.doc_id || "";
    const homeDoc: HomeDoc = {
      runId: run.run_id,
      docId,
      live: true,
      doctype: codeForBackendDocType(run.doc_type) ?? run.doc_type,
      backendDocType: run.doc_type,
      libraryId: run.library_id ?? null,
      libraryName: run.library_name?.trim() || null,
      title: run.name?.trim() || run.title || docId,
      sub: run.library_name?.trim() || run.project_name || "",
      state: mapRunStatusToDocState(run.status),
      stateLabel: statusToLabel(run.status),
      // Whole-submission denominator; see the finished branch above.
      progressLabel:
        (run.sections_total || run.modules_total) > 0
          ? `${run.modules_completed} of ${run.sections_total || run.modules_total} sections`
          : "—",
      pct:
        (run.sections_total || run.modules_total) > 0
          ? Math.round((run.modules_completed / (run.sections_total || run.modules_total)) * 100)
          : 0,
      gaps: 0,
      notes: 0,
      owner: ownerInitials,
      ownerBg: ownerBgColor,
      current: isCurrent(docId),
      createdAt: run.created_at ?? run.started_at ?? null,
      inputTokens: run.total_input_tokens ?? 0,
      outputTokens: run.total_output_tokens ?? 0,
      folderId: run.folder_id ?? null,
    };

    // A live run supersedes a completed entry on the same doc (show the
    // in-progress state), but must never clobber ANOTHER live run that
    // happens to share a doc_id — key the second one by run_id so both
    // remain visible.
    const existing = docId ? byDocId.get(docId) : undefined;
    if (existing?.live || !docId) {
      byDocId.set(`run:${run.run_id}`, homeDoc);
    } else {
      byDocId.set(docId, homeDoc);
    }
  }

  // Current document floats to the top; then live runs above completed ones.
  const result = Array.from(byDocId.values()).sort((a, b) => {
    if (a.current !== b.current) return a.current ? -1 : 1;
    if (a.live !== b.live) return a.live ? -1 : 1;
    return 0;
  });

  return result;
}

export function deriveDisplayFirstName(rawUsername: string | null | undefined): string | null {
  if (!rawUsername) return null;

  let firstSegment = rawUsername;

  const dotIdx = rawUsername.indexOf(".");
  const atIdx = rawUsername.indexOf("@");
  const spaceIdx = rawUsername.indexOf(" ");

  const separators = [dotIdx, atIdx, spaceIdx].filter((idx) => idx !== -1);
  if (separators.length > 0) {
    const firstSeparator = Math.min(...separators);
    firstSegment = rawUsername.substring(0, firstSeparator);
  }

  if (!firstSegment) return null;
  return firstSegment.charAt(0).toUpperCase() + firstSegment.slice(1).toLowerCase();
}

export function deriveInitials(rawUsername: string | null | undefined): string | null {
  if (!rawUsername) return null;

  // Remove email domain if present
  const username = rawUsername.indexOf("@") !== -1 ? rawUsername.substring(0, rawUsername.indexOf("@")) : rawUsername;

  // Split by dots or spaces to get name parts
  const parts = username.split(/[.\s_-]/).filter((p) => p.length > 0);

  if (parts.length === 0) return null;

  // If multiple words: take first letter of first and last word
  if (parts.length >= 2) {
    const first = parts[0].charAt(0).toUpperCase();
    const last = parts[parts.length - 1].charAt(0).toUpperCase();
    return `${first}${last}`;
  }

  // Single word: take first two letters
  if (parts[0].length >= 2) {
    return parts[0].substring(0, 2).toUpperCase();
  }

  // Single letter: take first letter twice
  return parts[0].charAt(0).toUpperCase() + parts[0].charAt(0).toUpperCase();
}

export function buildGreeting(now: Date, rawUsername: string | null | undefined): string {
  const hour = now.getHours();
  let period = "afternoon";
  if (hour < 12) {
    period = "morning";
  } else if (hour >= 17) {
    period = "evening";
  }

  const firstName = deriveDisplayFirstName(rawUsername);
  const namePart = firstName ? `, ${firstName}` : "";
  return `Good ${period}${namePart}.`;
}
